/**
 * Category URL slugs.
 *
 * The product JSON files use short data slugs that pre-date the SEO push
 * ("frites", "sauce", "dessert", "conserve"). We keep them as the single
 * source of truth — renaming them would force a CMS migration AND touch
 * every product fiche. Instead we add a public URL slug per category and
 * translate at the routing boundary.
 *
 * Public URLs (used in nav, footer, breadcrumb, canonical, sitemap):
 *   /catalogue/frites-surgelees
 *   /catalogue/sauces
 *   /catalogue/viandes
 *   /catalogue/volailles
 *   /catalogue/emballages
 *   /catalogue/tortillas-pains
 *   /catalogue/desserts
 *   /catalogue/appetizers
 *   /catalogue/specialites
 *   /catalogue/huile
 *   /catalogue/conserves
 *   /catalogue/produits-laitiers
 *
 * Legacy URLs `/catalogue?categorie=<data-slug>` still resolve in Catalog.jsx
 * (degrades gracefully when an old link is shared / bookmarked) but every
 * new internal link should use the URL slug above.
 */

// dataSlug → URL slug. Keys are what lives in product JSON / CategoryPicker.
export const CATEGORY_TO_URL_SLUG = {
  frites: "frites-surgelees",
  sauce: "sauces",
  viandes: "viandes",
  volailles: "volailles",
  emballages: "emballages",
  "tortillas-et-pains": "tortillas-pains",
  dessert: "desserts",
  appetizers: "appetizers",
  specialites: "specialites",
  huile: "huile",
  conserve: "conserves",
  "produits-laitiers": "produits-laitiers",
};

// URL slug → dataSlug. Used by Catalog.jsx when a /catalogue/:slug route fires.
export const URL_SLUG_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_URL_SLUG).map(([k, v]) => [v, k])
);

/**
 * Returns the public URL for a category, or /catalogue if the slug is unknown.
 * `null` / `undefined` / "all" → /catalogue (root).
 */
export function categoryPath(dataSlug) {
  if (!dataSlug || dataSlug === "all") return "/catalogue";
  const urlSlug = CATEGORY_TO_URL_SLUG[dataSlug];
  return urlSlug ? `/catalogue/${urlSlug}` : "/catalogue";
}

/**
 * Reverse: resolve a URL slug from /catalogue/:slug to a data slug.
 * Returns null when the URL slug is unknown (caller should 404).
 */
export function categoryFromUrlSlug(urlSlug) {
  if (!urlSlug) return null;
  return URL_SLUG_TO_CATEGORY[urlSlug] || null;
}
